import { QuickAddComponent } from '@theme/quick-add';

/**
 * Quick-add drawer — subscriptions (Made in Matcha).
 *
 * Relocates the SELECT ORDER widget into the drawer's own <form>, on the fetched clone, before
 * the vendor pipeline morphs it in.
 *
 * BACKGROUND. `snippets/custom.quick-add.liquid` no longer emits `data-uses-selling-plans`, so a
 * subscription product's Choose button now opens the drawer instead of navigating to the PDP (see
 * that file's doc for why the vendor guard does not apply to this store). The picker markup was
 * always in the fetched HTML — `[data-product-grid-content]` wraps `_product-details`, which
 * contains it — the guard simply short-circuited before it was used. Three things then stood
 * between "it is in the clone" and "it works", and one move fixes all three:
 *
 *   1. DESKTOP VISIBILITY. `snippets/quick-add-modal-styles.liquid:434-456` is a `*:not(…)`
 *      allowlist that `display: none !important`s everything in `.product-details` outside it.
 *      `custom.quick-add-drawer.liquid` keeps the `quick-add-modal__content` class deliberately, to
 *      inherit that filter. `.cds-plan-picker` is not on the list; `.buy-buttons-block *` is.
 *
 *   2. MOBILE SURVIVAL. Below 750px `assets/quick-add.js:219-247` lifts `variant-picker` and
 *      `product-form-component` out of `.product-details` and then removes `.product-details`
 *      outright. Anything left beside them is deleted before the morph.
 *
 *   3. FORM OWNERSHIP. The picker's `<select name="selling_plan">` reaches the buy form through
 *      `form="BuyButtons-ProductForm-<section.id>"`, i.e. by id. `templates/product.json` names
 *      that section `main` AND carries a product-recommendations grid, so opening the drawer from
 *      any PDP puts two `#BuyButtons-ProductForm-main` on the page — and association-by-id resolves
 *      to the first in tree order, the host PDP's. The drawer's own FormData would then carry no
 *      `selling_plan` and the shopper would get a one-time item under a UI reading "Subscription".
 *      Silent, and live the moment the guard comes off.
 *
 * Moving the picker inside the <form> puts it inside `.buy-buttons-block *` (1), onto the node the
 * rebuild lifts (2), and makes ownership structural instead of by-id (3). The `form` attribute has
 * to go with it: an explicit `form=` OVERRIDES the ancestor form, so the move means nothing while
 * it is still there.
 *
 * PRE-MORPH, NOT POST-MORPH. `assets/custom.quick-add-drawer.js` fixes things up after the fact
 * under a MutationObserver; this is the opposite hook, and deliberately a separate file. The
 * picker is removed from the clone BEFORE morph on mobile, so an observer could never restore it —
 * and a post-morph relocation would be undone and redone on every open, because morph reconciles
 * the live DOM back toward the un-relocated clone.
 *
 * WHY A PROTOTYPE PATCH AND NOT A FORK. `assets/quick-add.js` is vendor and deliberately not
 * forked (docs/hotspots.md #4 banks that: upstream fixes land automatically). Forking it to change
 * ~15 lines would also mean repointing the importmap and the script tag in `snippets/scripts.liquid`,
 * a second vendor file. `updateQuickAddModal` is a real prototype method, so wrapping it costs one
 * file and no vendor edits. (`handleClick` is a class *field* — per-instance, not on the prototype,
 * not patchable — which is why the navigation guard is lifted from Liquid instead.)
 *
 * Module order is guaranteed: `snippets/scripts.liquid` renders in <head> (`layout/theme.liquid:41`)
 * so `quick-add.js` evaluates first, and this loads from the drawer snippet near </body>. Prototype
 * methods resolve at call time, so patching after `customElements.define` is fine.
 */

/** Fixed, not random: the clone and the live DOM must agree across re-opens or morph will fight. */
const ID_SUFFIX = '-qa';

const original = QuickAddComponent.prototype.updateQuickAddModal;

if (typeof original !== 'function') {
  // An upstream rename would otherwise make this a silent no-op: the picker would vanish on mobile
  // and be display:none'd on desktop, with nothing in the console.
  console.warn(
    '[quick-add-plans] QuickAddComponent.prototype.updateQuickAddModal is missing — subscription ' +
      'products will not work in the quick-add drawer. See docs/hotspots.md #4.'
  );
} else {
  QuickAddComponent.prototype.updateQuickAddModal = function (productGrid) {
    relocatePicker(productGrid);
    return original.call(this, productGrid);
  };
}

/**
 * Moves the selling-plan picker into the buy form and de-duplicates its ids.
 *
 * `handleClick` clones from its cache on every open, so this always runs on a fresh copy and is
 * naturally idempotent — the cached original is never mutated.
 *
 * @param {Element} productGrid - The fetched `[data-product-grid-content]` clone.
 */
function relocatePicker(productGrid) {
  const picker = productGrid?.querySelector?.('.cds-plan-picker');
  if (!picker) return;

  const form = productGrid.querySelector('product-form-component form');
  if (!form) return;

  form.prepend(picker);

  // The whole point of the move — see FORM OWNERSHIP above.
  picker.querySelector('select[name="selling_plan"]')?.removeAttribute('form');

  suffixIds(picker);
}

/**
 * Suffixes the picker's own ids so the drawer copy cannot cross-wire with the PDP's.
 *
 * The `Cds*` ids are presentational now that the price switch matches on classes and values — the
 * only live one is the schedule `<label for>` — but a duplicate id still steals label clicks and
 * `getElementById`, so they are all renamed.
 *
 * `ProductInformation-*` is deliberately NOT touched: it is duplicated too, but
 * `snippets/custom.quick-add-drawer-styles.liquid` already carries an override keyed on it, and
 * renaming it here would silently turn that rule into dead code.
 *
 * @param {Element} picker - The relocated picker.
 */
function suffixIds(picker) {
  const renamed = new Set();

  for (const node of [picker, ...picker.querySelectorAll('[id^="Cds"]')]) {
    const id = node.getAttribute('id');
    if (!id || id.endsWith(ID_SUFFIX)) continue;

    node.setAttribute('id', id + ID_SUFFIX);
    renamed.add(id);
  }

  for (const label of picker.querySelectorAll('label[for]')) {
    const target = label.getAttribute('for');
    if (target && renamed.has(target)) label.setAttribute('for', target + ID_SUFFIX);
  }
}
